import React, { useState, useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import ContactForm from '../components/ContactForm';
import NotFound from './NotFound';

export default function PageRenderer() {
  const location = useLocation();
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  // Map URL path to CMS page_path
  const getPagePath = (pathname) => {
    if (pathname === '/') return 'home';
    if (pathname === '/om-solveig') return 'om-solveig';
    if (pathname === '/terapi') return 'terapi';
    if (pathname === '/terapi/familiebehandling') return 'familiebehandling';
    if (pathname === '/terapi/parterapi') return 'parterapi';
    if (pathname === '/terapi/traumer-og-seksuelle-overgreb-i-barndommen') return 'traumer-og-seksuelle-overgreb-i-barndommen';
    if (pathname === '/terapi/akut-terapi') return 'akut-terapi';
    if (pathname === '/kontakt') return 'kontakt';
    return null;
  };

  const pagePath = getPagePath(location.pathname);

  useEffect(() => {
    if (!pagePath) {
      setLoading(false);
      setError(true);
      return;
    }

    setLoading(true);
    setError(false);

    fetch(`https://api.dev.auxo.dk/public/v2/content/pages?page_path=${pagePath}`)
      .then((res) => {
        if (!res.ok) throw new Error('Failed to fetch page content');
        return res.json();
      })
      .then((data) => {
        if (data && data.items && data.items.length > 0) {
          const item = data.items[0];
          setPageData(item);
          
          // Update Document Metadata
          const title = item.meta_title?.da || item.title?.da || 'Solveig Juliane Tousgaard';
          const description = item.meta_description?.da || '';
          
          document.title = title;
          
          let metaDesc = document.querySelector('meta[name="description"]');
          if (!metaDesc) {
            metaDesc = document.createElement('meta');
            metaDesc.setAttribute('name', 'description');
            document.head.appendChild(metaDesc);
          }
          metaDesc.setAttribute('content', description);

          // OpenGraph
          let ogTitle = document.querySelector('meta[property="og:title"]');
          if (!ogTitle) {
            ogTitle = document.createElement('meta');
            ogTitle.setAttribute('property', 'og:title');
            document.head.appendChild(ogTitle);
          }
          ogTitle.setAttribute('content', title);

          let ogDesc = document.querySelector('meta[property="og:description"]');
          if (!ogDesc) {
            ogDesc = document.createElement('meta');
            ogDesc.setAttribute('property', 'og:description');
            document.head.appendChild(ogDesc);
          }
          ogDesc.setAttribute('content', description);
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setLoading(false);
      });
  }, [pagePath, location.pathname]);

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center bg-warm-light">
        <div className="flex flex-col items-center gap-4">
          <i className="fa-light fa-spinner-third animate-spin text-4xl text-warm-gray" />
          <span className="text-sm text-warm-gray font-medium tracking-wider uppercase">Henter indhold...</span>
        </div>
      </div>
    );
  }

  if (error || !pageData) {
    return <NotFound />;
  }

  const { title, hero_title, hero_subtitle, content, images } = pageData;
  const mainImage = images && images.length > 0 ? images[0] : null;

  // Helper to format image URLs with Cloudflare resizing
  const getResizedImage = (url, width = 800) => {
    if (!url) return '';
    if (url.startsWith('https://assets.auxodev.dk/')) {
      return url.replace('https://assets.auxodev.dk/', `https://assets.auxodev.dk/cdn-cgi/image/width=${width},quality=85,format=auto/`);
    }
    return url;
  };

  return (
    <div className="bg-warm-light min-h-screen">
      {/* Hero Section */}
      {pagePath === 'home' ? (
        <section className="relative min-h-[60vh] flex items-center overflow-hidden py-16 md:py-20">
          {/* Subtle warm background gradient instead of image */}
          <div className="absolute inset-0 z-0">
            {/* Subtle warm overlay */}
            <div className="absolute inset-0 bg-gradient-to-b from-warm-sand/20 via-warm-light/40 to-warm-light" />
            {/* Solid white bottom transition */}
            <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-warm-light to-transparent" />
            <div className="absolute bottom-0 left-0 right-0 h-6 bg-warm-light" />
          </div>
          
          <div className="max-w-7xl mx-auto px-6 relative z-10 w-full">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl lg:text-7xl text-warm-dark leading-tight font-display mb-6">
                {hero_title?.da || title?.da}
              </h1>
              {hero_subtitle?.da && (
                <p className="text-lg md:text-xl text-warm-gray font-light leading-relaxed mb-8 max-w-2xl mx-auto">
                  {hero_subtitle.da}
                </p>
              )}
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-3.5 bg-warm-dark text-warm-light text-sm font-medium tracking-wide rounded-full hover:bg-warm-gray transition-all-custom shadow-sm hover:shadow-md"
                >
                  Book en samtale
                </Link>
                <Link
                  to="/om-solveig"
                  className="px-8 py-3.5 border border-warm-dark text-warm-dark text-sm font-medium tracking-wide rounded-full hover:bg-warm-sand/20 transition-all-custom"
                >
                  Læs om Solveig
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* Main Content Layouts */}
      {pagePath === 'home' ? (
        /* FORSIDE (HOME) SPECIAL LAYOUT */
        <>
          {/* Core Areas Section */}
          <section className="py-24 bg-white border-y border-warm-sand/40">
            <div className="max-w-7xl mx-auto px-6">
              {/* Header Block */}
              <div className="max-w-3xl mb-16 text-center mx-auto">
                <span className="text-xs uppercase tracking-[0.25em] text-warm-gray font-semibold mb-3 block font-sans">
                  Fokusområder
                </span>
                <h2 className="text-3xl md:text-4xl lg:text-5xl font-light font-sans text-warm-dark mb-4 leading-tight">
                  Hvordan kan jeg hjælpe dig?
                </h2>
                <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans max-w-2xl mx-auto">
                  Jeg tilbyder specialiseret og nærværende terapi tilpasset dine eller jeres unikke behov. Herunder kan du læse mere om de områder, jeg primært arbejder med.
                </p>
              </div>

              {/* Feature Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6 lg:gap-8">
                {/* Area 1: Traumer & Overgreb */}
                <div className="bg-[#fdfcfb] border border-warm-sand/60 p-8 rounded-2xl flex flex-col justify-between group shadow-md hover:shadow-lg hover:border-warm-taupe transition-all duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-warm-sand/50 flex items-center justify-center text-warm-dark mb-6 text-xl">
                      <i className="fa-light fa-shield-heart" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-medium text-warm-dark mb-3 font-display">
                      Traumer & Overgreb
                    </h3>
                    <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans">
                      Bearbejdning af dybe sår og traumer for at genvinde trygheden og livskraften.
                    </p>
                  </div>
                  <Link
                    to="/terapi/traumer-og-seksuelle-overgreb-i-barndommen"
                    className="text-xs font-semibold text-white bg-warm-dark hover:bg-warm-gray border border-warm-dark hover:border-warm-gray rounded-full px-4 py-2 transition-all inline-flex items-center gap-1.5 group/link font-sans mt-auto w-fit"
                  >
                    Læs mere
                    <i className="fa-light fa-arrow-right text-[10px] transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>

                {/* Area 2: Familiebehandling */}
                <div className="bg-[#fdfcfb] border border-warm-sand/60 p-8 rounded-2xl flex flex-col justify-between group shadow-md hover:shadow-lg hover:border-warm-taupe transition-all duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-warm-sand/50 flex items-center justify-center text-warm-dark mb-6 text-xl">
                      <i className="fa-light fa-people-roof" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-medium text-warm-dark mb-3 font-display">
                      Familiebehandling
                    </h3>
                    <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans">
                      Støtte to at bryde fastlåste mønstre og styrke relationerne i familien.
                    </p>
                  </div>
                  <Link
                    to="/terapi/familiebehandling"
                    className="text-xs font-semibold text-white bg-warm-dark hover:bg-warm-gray border border-warm-dark hover:border-warm-gray rounded-full px-4 py-2 transition-all inline-flex items-center gap-1.5 group/link font-sans mt-auto w-fit"
                  >
                    Læs mere
                    <i className="fa-light fa-arrow-right text-[10px] transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>

                {/* Area 3: Parterapi */}
                <div className="bg-[#fdfcfb] border border-warm-sand/60 p-8 rounded-2xl flex flex-col justify-between group shadow-md hover:shadow-lg hover:border-warm-taupe transition-all duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-warm-sand/50 flex items-center justify-center text-warm-dark mb-6 text-xl">
                      <i className="fa-light fa-hands-holding-heart" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-medium text-warm-dark mb-3 font-display">
                      Parterapi
                    </h3>
                    <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans">
                      Redskaber til at genfinde nærheden, bryde negative cirkler og styrke jeres kommunikation.
                    </p>
                  </div>
                  <Link
                    to="/terapi/parterapi"
                    className="text-xs font-semibold text-white bg-warm-dark hover:bg-warm-gray border border-warm-dark hover:border-warm-gray rounded-full px-4 py-2 transition-all inline-flex items-center gap-1.5 group/link font-sans mt-auto w-fit"
                  >
                    Læs mere
                    <i className="fa-light fa-arrow-right text-[10px] transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>

                {/* Area 4: Individuel Terapi */}
                <div className="bg-[#fdfcfb] border border-warm-sand/60 p-8 rounded-2xl flex flex-col justify-between group shadow-md hover:shadow-lg hover:border-warm-taupe transition-all duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-warm-sand/50 flex items-center justify-center text-warm-dark mb-6 text-xl">
                      <i className="fa-light fa-seedling" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-medium text-warm-dark mb-3 font-display">
                      Individuel Terapi
                    </h3>
                    <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans">
                      Et trygt og nærværende rum til at arbejde med de udfordringer, der fylder i dit liv.
                    </p>
                  </div>
                  <Link
                    to="/terapi"
                    className="text-xs font-semibold text-white bg-warm-dark hover:bg-warm-gray border border-warm-dark hover:border-warm-gray rounded-full px-4 py-2 transition-all inline-flex items-center gap-1.5 group/link font-sans mt-auto w-fit"
                  >
                    Læs mere
                    <i className="fa-light fa-arrow-right text-[10px] transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>

                {/* Area 5: Akut Terapi */}
                <div className="bg-[#fdfcfb] border border-warm-sand/60 p-8 rounded-2xl flex flex-col justify-between group shadow-md hover:shadow-lg hover:border-warm-taupe transition-all duration-300">
                  <div>
                    <div className="w-12 h-12 rounded-full bg-warm-sand/50 flex items-center justify-center text-warm-dark mb-6 text-xl">
                      <i className="fa-light fa-clock" aria-hidden="true" />
                    </div>
                    <h3 className="text-xl font-medium text-warm-dark mb-3 font-display">
                      Akut Terapi
                    </h3>
                    <p className="text-sm text-warm-gray mb-6 leading-relaxed font-sans">
                      Hurtig hjælp, når livet slår revner. Du bliver kontaktet inden for 24 timer på hverdage.
                    </p>
                  </div>
                  <Link
                    to="/terapi/akut-terapi"
                    className="text-xs font-semibold text-white bg-warm-dark hover:bg-warm-gray border border-warm-dark hover:border-warm-gray rounded-full px-4 py-2 transition-all inline-flex items-center gap-1.5 group/link font-sans mt-auto w-fit"
                  >
                    Læs mere
                    <i className="fa-light fa-arrow-right text-[10px] transition-transform group-hover/link:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Intro Section */}
          <section className="py-20 max-w-5xl mx-auto px-6">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-14 items-center">
              {/* Solveig Portrait */}
              <div className="md:col-span-5 flex justify-center">
                <div className="relative group max-w-xs md:max-w-none w-full">
                  <div className="absolute -inset-2 bg-gradient-to-tr from-warm-sand/40 via-warm-taupe/20 to-warm-sand/10 rounded-2xl blur-md -z-10" />
                  <div className="overflow-hidden rounded-2xl border border-warm-sand/40 shadow-md bg-white">
                    <img
                      src={getResizedImage('https://assets.auxodev.dk/assets/03b9ae14-e37a-4264-bc6f-37708de40179/mpfsolveig.jpeg', 800)}
                      alt="Solveig Juliane Tousgaard - Familie og Psykoterapeut MPF"
                      className="w-full h-auto object-cover object-center max-h-[440px] md:max-h-[500px]"
                    />
                    <div className="p-4 bg-white/95 text-center border-t border-warm-sand/30">
                      <p className="font-display font-medium text-warm-dark text-base">Solveig Juliane Tousgaard</p>
                      <p className="text-xs text-warm-gray">Familie- og Psykoterapeut MPF</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Text Block */}
              <div className="md:col-span-7 flex flex-col gap-6">
                <h2 className="text-3xl md:text-4xl text-warm-dark">
                  Velkommen til min praksis i Middelfart
                </h2>
                <div 
                  className="text-warm-gray text-base leading-relaxed space-y-4
                    [&_h2]:font-display [&_h2]:text-2xl [&_h2]:text-warm-dark [&_h2]:font-medium [&_h2]:mt-6 [&_h2]:mb-3
                    [&_h3]:font-display [&_h3]:text-xl [&_h3]:text-warm-dark [&_h3]:font-medium [&_h3]:mt-4 [&_h3]:mb-2
                    [&_strong]:text-warm-dark [&_strong]:font-semibold
                    [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-1.5 [&_ul]:my-3
                    [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-1.5 [&_ol]:my-3
                    [&_li]:text-warm-gray [&_li]:leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: content?.da || '' }}
                />
                <div className="pt-2">
                  <Link
                    to="/om-solveig"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-warm-dark hover:text-warm-gray transition-colors group"
                  >
                    Læs mere om min baggrund og faglige tilgang
                    <i className="fa-light fa-arrow-right transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Call to Action Section */}
          <section className="py-12 md:py-16 w-full bg-warm-sand/20 border-t border-b border-warm-sand/30 text-center relative overflow-hidden">
            <div className="absolute -top-24 -left-24 w-48 h-48 rounded-full bg-warm-taupe/20 blur-2xl" />
            <div className="absolute -bottom-24 -right-24 w-48 h-48 rounded-full bg-warm-taupe/20 blur-2xl" />
            
            <div className="max-w-4xl mx-auto px-6 relative z-10">
              <h2 className="text-2xl md:text-3xl text-warm-dark mb-4">
                Har du brug for en fortrolig samtale?
              </h2>
              <p className="text-sm text-warm-gray max-w-xl mx-auto leading-relaxed mb-6">
                Du er altid velkommen til at række ud for en uforpligtende snak eller for at booke en tid i min klinik i Middelfart. Jeg glæder mig til at møde dig.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link
                  to="/kontakt"
                  className="px-8 py-3.5 bg-warm-dark text-warm-light text-sm font-medium tracking-wide rounded-full hover:bg-warm-gray transition-all-custom shadow-sm"
                >
                  Kontakt Solveig
                </Link>
                <a
                  href="tel:23369087"
                  className="px-8 py-3.5 bg-transparent border border-warm-dark/30 text-warm-dark text-sm font-medium tracking-wide rounded-full hover:bg-warm-sand/20 transition-all-custom whitespace-nowrap"
                >
                  Ring: 23 36 90 87
                </a>
              </div>
            </div>
          </section>
        </>
      ) : pagePath === 'kontakt' ? (
        /* KONTAKT PAGE LAYOUT */
        <section className="py-20 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Contact Info Column */}
            <div className="lg:col-span-6 flex flex-col gap-8">
              <div>
                <h2 className="text-3xl text-warm-dark mb-4">
                  Find vej & Kontakt
                </h2>
                <p className="text-sm text-warm-gray leading-relaxed">
                  Min klinik er beliggende i rolige og diskrete omgivelser i Middelfart. Der er gode parkeringsforhold lige ved døren, og klinikken er let tilgængelig.
                </p>
              </div>

              <div className="flex flex-col divide-y divide-warm-sand/50">
                {/* Address */}
                <div className="flex gap-4 items-start py-6 first:pt-0 last:pb-0">
                  <div className="text-warm-dark shrink-0 pt-1 w-6 flex justify-center">
                    <i className="fa-light fa-location-dot text-xl" />
                  </div>
                  <div>
                    <p className="text-sm text-warm-dark leading-relaxed">
                      Møgelvænget 4<br />
                      5500 Middelfart
                    </p>
                  </div>
                </div>

                {/* Phone */}
                <div className="flex gap-4 items-start py-6 first:pt-0 last:pb-0">
                  <div className="text-warm-dark shrink-0 pt-1 w-6 flex justify-center">
                    <i className="fa-light fa-phone text-xl" />
                  </div>
                  <div>
                    <a href="tel:23369087" className="text-sm text-warm-dark hover:text-warm-gray transition-colors font-medium">
                      23 36 90 87
                    </a>
                    <p className="text-xs text-warm-gray mt-1">Læg gerne en besked, hvis jeg er optaget af samtaler.</p>
                  </div>
                </div>

                {/* Email */}
                <div className="flex gap-4 items-start py-6 first:pt-0 last:pb-0">
                  <div className="text-warm-dark shrink-0 pt-1 w-6 flex justify-center">
                    <i className="fa-light fa-envelope text-xl" />
                  </div>
                  <div>
                    <a href="mailto:solveig.tousgaard@gmail.com" className="text-sm text-warm-dark hover:text-warm-gray transition-colors font-medium">
                      solveig.tousgaard@gmail.com
                    </a>
                  </div>
                </div>

                {/* Facebook */}
                <div className="flex gap-4 items-start py-6 first:pt-0 last:pb-0">
                  <div className="text-warm-dark shrink-0 pt-1 w-6 flex justify-center">
                    <i className="fa-brands fa-facebook text-xl text-[#1877F2]" />
                  </div>
                  <div>
                    <a 
                      href="https://www.facebook.com" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-sm text-warm-dark hover:text-warm-gray transition-colors font-medium"
                    >
                      Følg min Facebook-side
                    </a>
                    <p className="text-xs text-warm-gray mt-1">Her deler jeg løbende tanker, artikler og opdateringer.</p>
                  </div>
                </div>
              </div>

              {/* Priser & Praktisk Section on Contact Page */}
              <div className="mt-8 bg-white border border-warm-sand/30 p-6 shadow-sm">
                <h3 className="text-lg font-medium text-warm-dark mb-4 font-display">
                  Priser & Praktisk
                </h3>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark">Individuel terapi</span>
                    <span className="text-sm text-warm-dark font-semibold">500 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark">Parterapi</span>
                    <span className="text-sm text-warm-dark font-semibold">900 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark">Familiebehandling</span>
                    <span className="text-sm text-warm-dark font-semibold">1.000 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark">Studierabat / pensionist</span>
                    <span className="text-sm text-warm-dark font-semibold">450 kr.</span>
                  </div>
                </div>
                <div className="bg-warm-sand/10 py-2.5 px-3 rounded-sm">
                  <p className="text-xs text-warm-gray font-medium">
                    <i className="fa-light fa-calendar-xmark mr-1.5 text-warm-dark" />
                    Afbud senest dagen før.
                  </p>
                </div>
              </div>
            </div>

            {/* Form Column */}
            <div className="lg:col-span-6">
              <ContactForm />
            </div>
          </div>
        </section>
      ) : (
        /* STANDARD CONTENT LAYOUT (Om Solveig, Terapi, Familiebehandling, Parterapi, Traumer) */
        <section className="py-20 max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Rich Text Content */}
            <div className="lg:col-span-7 flex flex-col gap-8">
              <div>
                <span className="text-xs uppercase tracking-[0.25em] text-warm-gray font-semibold mb-3 inline-block">
                  {pagePath.startsWith('terapi') || ['familiebehandling', 'parterapi', 'traumer-og-seksuelle-overgreb-i-barndommen', 'akut-terapi'].includes(pagePath) ? 'Terapi & Behandling' : 'Solveig Juliane Tousgaard'}
                </span>
                <h1 className="text-4xl md:text-5xl text-warm-dark leading-tight font-display mb-4">
                  {title?.da}
                </h1>
                {hero_subtitle?.da && (
                  <p className="text-base text-warm-gray font-light leading-relaxed">
                    {hero_subtitle.da}
                  </p>
                )}
              </div>

              <div 
                className="text-warm-gray text-base leading-relaxed space-y-6
                  [&_h2]:font-display [&_h2]:text-3xl [&_h2]:text-warm-dark [&_h2]:font-medium [&_h2]:mt-8 [&_h2]:mb-4 [&_h2]:border-b [&_h2]:border-warm-sand/50 [&_h2]:pb-2
                  [&_h3]:font-display [&_h3]:text-2xl [&_h3]:text-warm-dark [&_h3]:font-medium [&_h3]:mt-6 [&_h3]:mb-3
                  [&_strong]:text-warm-dark [&_strong]:font-semibold
                  [&_p]:leading-relaxed [&_p]:mb-4
                  [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:space-y-2 [&_ul]:my-4
                  [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:space-y-2 [&_ol]:my-4
                  [&_li]:text-warm-gray [&_li]:leading-relaxed"
                dangerouslySetInnerHTML={{ __html: content?.da || '' }}
              />
              
              {/* Bottom CTA on subpages */}
              <div className="mt-8 p-6 md:p-8 bg-white border border-warm-sand/30 rounded-none flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm">
                <div>
                  <h4 className="font-display text-xl text-warm-dark mb-1">Har du spørgsmål eller ønsker du at booke?</h4>
                  <p className="text-xs text-warm-gray">Tag det første skridt mod en lettere hverdag. Jeg er her for at hjælpe dig.</p>
                </div>
                <Link
                  to="/kontakt"
                  className="px-6 py-3 bg-warm-dark text-warm-light text-xs font-medium tracking-wide rounded-full hover:bg-warm-gray transition-all-custom whitespace-nowrap shadow-sm"
                >
                  Kontakt mig her
                </Link>
              </div>
            </div>

            {/* Sidebar Content */}
            <div className="lg:col-span-5 lg:sticky lg:top-28">
              {/* Featured Image Card (if page has an image, e.g. Om Solveig) */}
              {mainImage && (
                <div className="bg-white border border-warm-sand/30 p-3 mb-8 shadow-sm rounded-none overflow-hidden text-center">
                  <img
                    src={getResizedImage(mainImage, 800)}
                    alt={title?.da || 'Solveig Juliane Tousgaard'}
                    className="w-full h-auto object-cover max-h-[460px] mb-3"
                  />
                  <div className="p-2">
                    <p className="font-display text-lg font-medium text-warm-dark">Solveig Juliane Tousgaard</p>
                    <p className="text-xs text-warm-gray font-sans mt-0.5">Familie- og Psykoterapeut MPF</p>
                  </div>
                </div>
              )}

              {/* Priser & Praktisk Card */}
              <div className="bg-white border border-warm-sand/30 p-6 mb-8 shadow-sm">
                <span className="text-[10px] uppercase tracking-[0.2em] text-warm-gray font-semibold mb-4 block text-center">
                  Priser & Praktisk
                </span>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark font-medium">Individuel terapi</span>
                    <span className="text-sm text-warm-dark font-semibold">500 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark font-medium">Parterapi</span>
                    <span className="text-sm text-warm-dark font-semibold">900 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark font-medium">Familiebehandling</span>
                    <span className="text-sm text-warm-dark font-semibold">1.000 kr.</span>
                  </div>
                  <div className="flex justify-between items-center border-b border-warm-sand/20 pb-2">
                    <span className="text-sm text-warm-dark font-medium">Studierabat / pensionist</span>
                    <span className="text-sm text-warm-dark font-semibold">450 kr.</span>
                  </div>
                </div>
                <div className="text-center bg-warm-sand/10 py-2.5 px-3 rounded-sm">
                  <p className="text-xs text-warm-gray font-medium">
                    <i className="fa-light fa-calendar-xmark mr-1.5 text-warm-dark" />
                    Afbud senest dagen før.
                  </p>
                </div>
              </div>

              {/* MPF Certification Badge */}
              <div className="bg-white border border-warm-sand/30 p-6 text-center flex flex-col items-center shadow-sm">
                <span className="text-[10px] uppercase tracking-[0.2em] text-warm-gray font-semibold mb-3">
                  Medlem af Psykoterapeutforeningen
                </span>
                <img
                  src={getResizedImage('https://assets.auxodev.dk/assets/03b9ae14-e37a-4264-bc6f-37708de40179/skaermbillede-2025-03-03-kl-09-55-27.png', 240)}
                  alt="Psykoterapeutforeningen MPF"
                  className="h-14 w-auto object-contain mb-3"
                />
                <p className="text-xs text-warm-gray leading-relaxed max-w-xs">
                  Titlen MPF er din garanti for en godkendt, professionel og kvalitetssikret uddannelse samt løbende supervision.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
